import React, { useState, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";

// Utils
import areObjectsEqual from "../../../../utils/areObjectsEqual";
import { sanitizeFileName } from "../../../../utils";
import Spinner from "../../../Template/Components/Spinner";
import { maxFileSizeValidation } from "../../../../utils/common";

// Constants
const MAX_NAME_LENGTH = 25;

// Initial error state
const INITIAL_ERRORS = {
  name: false,
  nameExists: false,
  image: false,
};

/**
 * Form component for creating/editing patterns (base images)
 */
const Form = ({ handleSubmit, formData, onDiscard, typeCheck, artPackId }) => {
  const [form, setForm] = useState(formData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  /**
   * Check if there are unsaved changes
   */
  const changesFound = useMemo(
    () => !areObjectsEqual({ ...formData }, form),
    [formData, form]
  );

  /**
   * Check if the pattern name exists
   */
  const checkNameUniqueness = useCallback(async (name, id, artPackId) => {
    try {
      const encodedName = encodeURIComponent(name);
      const res = await fetch(`/api/pattern/checkname/${encodedName}/${id}/${artPackId}`);
      if (res.status === 200) {
        const data = await res.json();
        return data.checkStatus || false;
      }
      return false;
    } catch (error) {
      console.error("Error checking name uniqueness:", error);
      return false;
    }
  }, []);

  /**
   * Upload image to server
   */
  const uploadImage = useCallback(async (file, encryptedName) => {
    try {
      const formData = new FormData();
      formData.append("file", file, encryptedName);
      return axios.post(`/api/file/upload`, formData);
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  }, []);

  /**
   * Validate form data
   */
  const validateForm = useCallback(async () => {
    const newErrors = { ...INITIAL_ERRORS };

    // Validate pattern name
    if (!form.pattern_name) {
      newErrors.name = true;
    }

    // Validate image
    if (!form.uploadedfile) {
      newErrors.image = true;
    }

    // Check name uniqueness
    if (form.pattern_name) {
      const checkId = typeCheck === "createItem" ? 0 : form.pattern_id;
      const isNameExists = await checkNameUniqueness(form.pattern_name, checkId, artPackId);
      newErrors.nameExists = isNameExists;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error === true);
  }, [form, typeCheck, artPackId, checkNameUniqueness]);

  /**
   * Handle changes to form inputs
   */
  const handleChange = useCallback((e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    // Reset errors when user makes changes
    setErrors(INITIAL_ERRORS);
  }, []);

  /**
   * Handle form submission
   */
  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload image if there's a new file
      if (form.uploadedfile) {
        await uploadImage(form.uploadedfile, form.thumbnail_url);
      }

      // Validate form
      const isValid = await validateForm();

      // If validation passes, submit the form
      if (isValid) {
        handleSubmit(form);
        onDiscard();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  }, [form, uploadImage, validateForm, handleSubmit, onDiscard]);

  /**
   * Handle file drop in dropzone
   */
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setForm((prev) => ({
        ...prev,
        uploadedfile: file,
        thumbnail_url: sanitizeFileName(file?.name),
        thumbnail_url_preview: URL.createObjectURL(file),
      }));
      setErrors(INITIAL_ERRORS);
    }
  }, []);

  /**
   * Handle image change from file input
   */
  const onImageChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    const error = maxFileSizeValidation(selectedFile);

    if (selectedFile && !error) {
      setForm((prev) => ({
        ...prev,
        uploadedfile: selectedFile,
        thumbnail_url: sanitizeFileName(selectedFile?.name),
        thumbnail_url_preview: URL.createObjectURL(selectedFile),
      }));
      setErrors(INITIAL_ERRORS);
    } else if (error) {
      setErrors((prev) => ({ ...prev, image: error }));
    }
  }, []);

  /**
   * Configure dropzone
   */
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  /**
   * Get error message for image validation
   */
  const imageErrorMessage = useMemo(() => {
    if (!errors.image) return null;
    return typeof errors.image === "string" ? errors.image : "Base Image is required";
  }, [errors.image]);

  return (
    <>
      {/* Pattern Name Input */}
      <div className="mt-4">
        <label htmlFor="pattern_name" className="form-labels">
          Name
        </label>
        <input
          type="text"
          name="pattern_name"
          id="pattern_name"
          placeholder=""
          onChange={handleChange}
          value={form.pattern_name || ""}
          className="form-control inputs"
          maxLength={MAX_NAME_LENGTH}
        />
        {errors.name && (
          <span id="errorUrl" className="form-text text-danger" style={{ display: "block" }}>
            Name is required
          </span>
        )}
        {errors.nameExists && (
          <span className="form-text text-danger" style={{ display: "block" }}>
            This Art Base Image Name already exists, please use a different one.
          </span>
        )}
      </div>

      {/* Thumbnail URL Input */}
      <div
        className="mt-5 mb-2 upload-image-group"
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto 1fr",
          gridTemplateRows: "auto auto",
          alignItems: "start",
          gap: "1rem",
          width: "100%",
        }}
      >
        {/* Upload Button */}
        <div>
          <label className="btn btn-upload form-btn">
            Upload Image
            <input
              type="file"
              name="ct_icon_nft"
              accept="image/*"
              onChange={onImageChange}
            />
          </label>
        </div>

        {/* OR Separator */}
        <span style={{ fontSize: "1.5rem", alignSelf: "start" }}>or</span>

        {/* Dropzone */}
        <div style={{ width: "100%" }}>
          <div
            {...getRootProps()}
            style={{
              border: form.thumbnail_url_preview ? "1px solid #70707099" : "1px dashed #70707099",
              padding: form.thumbnail_url_preview ? "2rem" : "4rem 2rem",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#ededed",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <input {...getInputProps()} />
            {form.thumbnail_url_preview ? (
              <img
                src={form.thumbnail_url_preview}
                className="custom-choose-file"
                alt="Preview of the uploaded thumbnail"
              />
            ) : (
              <p style={{ fontSize: "1.5rem", color: "#707070" }}>
                Drag and drop Base Image here
              </p>
            )}
          </div>
        </div>

        {/* Error Message */}
        <div
          style={{
            gridColumn: "3",
            gridRow: "2",
            width: "100%",
            textAlign: "right",
          }}
        >
          {imageErrorMessage && (
            <span
              id="errorPatternImage"
              className="form-text text-danger"
              style={{ display: "block" }}
            >
              {imageErrorMessage}
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pt-5">
        <button
          type="button"
          className="btn btn-secondary form-btn me-4"
          onClick={onDiscard}
          aria-label="Discard changes"
        >
          Discard
        </button>
        <button
          type="submit"
          className="btn bg-orange form-btn"
          onClick={onSubmit}
          disabled={!changesFound}
          aria-label="Save changes"
        >
          Save Changes
        </button>
      </div>

      {loading && <Spinner spinner={loading} />}
    </>
  );
};

export default Form;
